import type {ChangePreviewDelta} from 'src/theme/semantic-colors'

import type {PreviewOutcome, ScheduleRole, UnchangedReason} from './schemas'

export const PREVIEW_OUTCOME_LABELS: Record<PreviewOutcome, string> = {
    created: 'Added',
    updated: 'Updated',
    removed: 'Removed',
    unlinked: 'Kept (unlinked)',
    unchanged: 'Unchanged',
}

export const UNCHANGED_REASON_LABELS: Record<UnchangedReason, string> = {
    worked: 'Worked',
    past: 'In the past',
    skipped: 'Skipped',
    already_current: 'Unchanged',
    unaffected: 'Unaffected',
}

/** The eligibility rule made readable — shown under each left-alone visit. */
export const UNCHANGED_REASON_DETAILS: Record<UnchangedReason, string> = {
    worked: 'Completed, has logged hours, or equipment is deployed',
    past: 'Visit date has already passed',
    skipped: 'Canceled occurrence — restore it any time',
    already_current: 'This job is already part of the existing schedule. No changes needed.',
    unaffected: 'Not governed by this change',
}

export const PREVIEW_OUTCOME_DETAILS: Record<PreviewOutcome, string> = {
    created: 'A new visit for a date the schedule now covers',
    updated: 'Re-stamped with the new requirements, notes, or date',
    removed: 'Dropped — planned, with no work against it',
    unlinked: 'Kept on the board but detached from the schedule',
    unchanged: 'Left exactly as it is',
}

/** The count badges speak the delta vocabulary, so they explain themselves in the same words. */
export const PREVIEW_DELTA_DETAILS: Record<ChangePreviewDelta, string> = {
    added: PREVIEW_OUTCOME_DETAILS.created,
    updated: PREVIEW_OUTCOME_DETAILS.updated,
    removed: PREVIEW_OUTCOME_DETAILS.removed,
    kept: PREVIEW_OUTCOME_DETAILS.unlinked,
    unchanged: PREVIEW_OUTCOME_DETAILS.unchanged,
}

export const SCHEDULE_ROLE_LABELS: Record<ScheduleRole, string> = {
    deploy: 'Deploy',
    service: 'Service',
    pickup: 'Pickup',
}

const OUTCOME_DELTAS: Record<PreviewOutcome, ChangePreviewDelta> = {
    created: 'added',
    updated: 'updated',
    removed: 'removed',
    unlinked: 'kept',
    unchanged: 'unchanged',
}

export function previewOutcomeDelta(outcome: PreviewOutcome): ChangePreviewDelta {
    return OUTCOME_DELTAS[outcome]
}

export function previewRowAnnotation(
    outcome: PreviewOutcome,
    reason: UnchangedReason | null,
): string {
    if (outcome === 'unchanged' && reason != null) {
        return UNCHANGED_REASON_LABELS[reason]
    }

    return PREVIEW_OUTCOME_LABELS[outcome]
}

/** The one-line "why" under a row: reason detail when left alone, outcome detail otherwise. */
export function previewRowDetail(outcome: PreviewOutcome, reason: UnchangedReason | null): string {
    if (outcome === 'unchanged' && reason != null) {
        return UNCHANGED_REASON_DETAILS[reason]
    }

    return PREVIEW_OUTCOME_DETAILS[outcome]
}

export const SCHEDULE_LOCKED_JOB_HINT =
    'Requirements, notes, and date are governed by the service schedule. Add a comment for one-off details, or duplicate this job to customize it.'

export function isScheduleLockedServiceJob(scheduleRole: string | null | undefined): boolean {
    return scheduleRole === 'service'
}
